import { SECTION_META, SECTION_VARIANTS, type SectionNode, type SectionType, type WebsiteDefinition } from "@baze/schema";
import { resolveVariant, substitute, type Design, type StockImage, type Template } from "@baze/catalog";
import { defaultAnchor, nextSectionId, uniqueAnchor } from "./ids";

type Json = Parameters<typeof substitute>[0];

/**
 * Generic Indonesian starter content per section type, used when the site's template has no section of
 * that type. Every entry satisfies the section content schema (tests assert this). Testimonials are
 * marked `sample: true` so the publish checklist warns until they are replaced.
 */
export function genericContent(type: SectionType, stock: StockImage[] = []): Record<string, unknown> {
  const img = (i: number) => {
    const s = stock[i % Math.max(1, stock.length)];
    return s ? { src: s.src, alt: s.alt, width: s.width, height: s.height } : undefined;
  };
  const withImg = <T extends Record<string, unknown>>(o: T, i: number) => {
    const im = img(i);
    return im ? { ...o, image: im } : o;
  };
  switch (type) {
    case "navbar":
      return { showCta: true, ctaLabel: "Hubungi kami", showTitle: true };
    case "hero":
      return withImg(
        {
          eyebrow: "Selamat datang",
          title: "Tulis judul utama yang menjelaskan usaha Anda",
          description: "Satu-dua kalimat tentang apa yang Anda tawarkan dan kenapa pelanggan memilih Anda.",
          primaryCta: { label: "Chat WhatsApp", kind: "whatsapp", message: "Halo {{nama_usaha}}, saya mau tanya." },
        },
        0,
      );
    case "about":
      return withImg(
        {
          eyebrow: "Tentang kami",
          title: "Cerita di balik usaha kami",
          body: "Ceritakan sejak kapan usaha Anda berdiri, apa yang membuatnya berbeda, dan bagaimana Anda melayani pelanggan.",
          highlights: ["Pelayanan ramah", "Harga jujur", "Siap membantu"],
        },
        1,
      );
    case "services":
      return {
        eyebrow: "Layanan",
        title: "Layanan kami",
        items: [
          { title: "Layanan utama", description: "Jelaskan layanan ini dalam satu kalimat." },
          { title: "Layanan kedua", description: "Apa yang pelanggan dapatkan?" },
          { title: "Layanan ketiga", description: "Tambahkan harga jika ada." },
        ],
      };
    case "menu":
      return {
        eyebrow: "Menu",
        title: "Menu favorit",
        categories: [{ name: "Menu utama", items: [{ name: "Menu andalan", description: "Deskripsi singkat menu." }, { name: "Menu kedua" }] }],
        orderMessage: "Halo {{nama_usaha}}, saya mau pesan {{produk}}.",
      };
    case "products":
      return {
        eyebrow: "Produk",
        title: "Produk kami",
        items: [withImg({ name: "Produk unggulan", description: "Deskripsi singkat produk." }, 0), withImg({ name: "Produk kedua" }, 1)],
        orderMessage: "Halo {{nama_usaha}}, saya mau pesan {{produk}}.",
      };
    case "logos":
      return {
        title: "Dipercaya & bekerja sama dengan",
        grayscale: true,
        items: ["Mitra satu", "Mitra dua", "Mitra tiga", "Mitra empat", "Mitra lima", "Mitra enam"].map((name) => ({ name })),
      };
    case "gallery":
      return { eyebrow: "Galeri", title: "Suasana kami", images: [img(0), img(1), img(2)].filter(Boolean) };
    case "testimonials":
      return {
        eyebrow: "Testimoni",
        title: "Kata pelanggan",
        items: [
          { quote: "Contoh ulasan: pelayanannya cepat dan ramah. Ganti dengan ulasan asli pelanggan Anda.", name: "Nama pelanggan", rating: 5, sample: true },
          { quote: "Contoh ulasan kedua. Minta izin pelanggan sebelum menampilkan ulasannya.", name: "Nama pelanggan", rating: 5, sample: true },
        ],
      };
    case "faq":
      return {
        eyebrow: "FAQ",
        title: "Pertanyaan yang sering ditanyakan",
        items: [
          { question: "Bagaimana cara memesan?", answer: "Tekan tombol WhatsApp, lalu sampaikan pesanan Anda. Kami balas secepatnya." },
          { question: "Jam berapa Anda buka?", answer: "Lihat jam buka kami di bagian bawah halaman ini." },
        ],
      };
    case "stats":
      return { items: [{ value: "5+", label: "Tahun melayani" }, { value: "100%", label: "Bahan pilihan" }, { value: "7 hari", label: "Buka setiap minggu" }] };
    case "opening-hours":
      return { eyebrow: "Jam buka", title: "Kapan kami buka" };
    case "location":
      return { eyebrow: "Lokasi", title: "Kunjungi kami", showMap: true };
    case "contact":
      return { eyebrow: "Kontak", title: "Hubungi kami", description: "Ada pertanyaan? Kami siap membantu.", showWhatsapp: true, showPhone: true, showEmail: true, showSocial: true };
    case "pricing":
      return {
        eyebrow: "Harga",
        title: "Pilih paket",
        plans: [
          { name: "Basic", features: ["Fitur utama", "Dukungan via WhatsApp"] },
          { name: "Lengkap", features: ["Semua fitur Basic", "Bonus tambahan"], highlighted: true },
        ],
      };
    case "cta":
      return { title: "Siap memesan?", description: "Hubungi kami sekarang, kami balas dengan cepat.", button: { label: "Chat sekarang", kind: "whatsapp" } };
    case "timeline":
      return {
        eyebrow: "Cara kerja",
        title: "Langkah demi langkah",
        items: [
          { label: "Langkah 1", title: "Konsultasi", description: "Ceritakan kebutuhan Anda lewat WhatsApp." },
          { label: "Langkah 2", title: "Penawaran", description: "Kami kirim rincian dan jadwal." },
          { label: "Langkah 3", title: "Pengerjaan", description: "Kami kerjakan sesuai kesepakatan." },
        ],
      };
    case "events":
      return {
        eyebrow: "Acara",
        title: "Jadwal acara",
        showCalendar: true,
        items: [{ name: "Nama acara", startTime: "09:00", timeNote: "WIB", venue: "Nama tempat", address: "Alamat lengkap" }],
      };
    case "team":
      return {
        eyebrow: "Tim",
        title: "Orang di balik layanan kami",
        items: [withImg({ name: "Nama", role: "Peran" }, 0), withImg({ name: "Nama", role: "Peran" }, 1)],
      };
    case "quote":
      return { text: "Tulis kutipan, prinsip, atau janji Anda kepada pelanggan di sini.", cite: "Nama Anda" };
    case "countdown": {
      // Three months from today: a sensible placeholder the owner replaces with the real date.
      const d = new Date(Date.now() + 90 * 86_400_000);
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return { eyebrow: "Menuju hari bahagia", title: "Hitung mundur", date, time: "09:00", timeNote: "WIB", showCalendar: true };
    }
    case "gift":
      return {
        eyebrow: "Tanda kasih",
        title: "Kirim hadiah",
        description: "Bila ingin memberi tanda kasih, bisa melalui:",
        accounts: [{ provider: "Nama bank", name: "Nama pemilik rekening", number: "0000000000" }],
      };
    case "donation":
      return {
        eyebrow: "Donasi",
        title: "Salurkan donasi Anda",
        description: "Isi formulir di bawah. Setelah dikirim, kami konfirmasi lewat WhatsApp.",
        accounts: [{ provider: "Nama bank", name: "Nama pemilik rekening", number: "0000000000" }],
        presets: [50_000, 100_000, 250_000],
      };
    case "blog-latest":
      return { eyebrow: "Blog", title: "Artikel terbaru", count: 3 };
    case "wishes":
      return { eyebrow: "Ucapan & doa", title: "Kirim ucapan", description: "Doa dan ucapan Anda sangat berarti bagi kami." };
    case "rsvp":
      return { eyebrow: "Konfirmasi kehadiran", title: "RSVP", description: "Mohon kabari kami apakah Anda dapat hadir.", maxPax: 2 };
    case "links":
      return {
        items: [
          { label: "Toko online saya", href: "https://shopee.co.id/", description: "Ganti dengan tautan tokomu", highlight: true },
          { label: "Video terbaru", href: "https://www.youtube.com/" },
          { label: "Kerja sama & endorse", href: "https://example.com", description: "Ganti dengan tautan kontakmu" },
        ],
      };
    case "picks":
      return {
        eyebrow: "Rekomendasi",
        title: "Barang favoritku",
        items: [
          withImg({ name: "Nama produk", description: "Kenapa kamu suka produk ini.", href: "https://shopee.co.id/" }, 0),
          withImg({ name: "Nama produk kedua", href: "https://www.tokopedia.com/" }, 1),
        ],
      };
    case "product-feature":
      return withImg(
        {
          eyebrow: "E-book",
          title: "Judul yang menjawab masalah pembeli",
          description: "Ceritakan siapa yang cocok membeli dan apa yang berubah setelah membacanya.",
          bullets: ["Isi utama yang didapat", "Bonus atau template pendukung", "Bisa dibaca di HP dan laptop"],
          price: 79000,
          showTiers: true,
        },
        0,
      );
    case "videos":
      return {
        eyebrow: "Video",
        title: "Tonton cerita kami",
        description: "Tempel tautan video YouTube untuk menampilkannya di sini.",
        items: [{ title: "Video pertama" }, { title: "Video kedua" }, { title: "Video ketiga" }],
      };
    case "lessons":
      return {
        eyebrow: "Materi",
        title: "Daftar materi",
        description: "Pilih topik yang ingin dipelajari.",
        groups: [
          {
            label: "Pertemuan 1",
            title: "Judul pertemuan",
            topics: [
              { title: "Topik pertama", summary: "Ringkasan singkat topik ini.", points: ["Poin penting pertama", "Poin penting kedua"], body: "Tulis isi materi di sini." },
              { title: "Topik kedua", summary: "Ringkasan singkat topik ini." },
            ],
          },
        ],
      };
    case "footer":
      return { showSocial: true, showHours: true, showAddress: true, showNav: true, showContact: true, showTitle: true };
  }
}

export interface DefaultsContext {
  template?: Template;
  design?: Design;
  stock?: StockImage[];
  industryName?: string;
  anchors?: Partial<Record<SectionType, string>>;
}

/** The template's own section of this type (first match across its pages), if any. */
function templateSection(template: Template | undefined, type: SectionType) {
  for (const page of template?.pages ?? []) {
    const s = page.sections.find((x) => x.type === type);
    if (s) return s;
  }
  return undefined;
}

/**
 * A new, meaningful section of `type` for `pageSections`: template content when available (placeholders
 * filled with the site's name/city), otherwise generic defaults. Unique id + anchor.
 */
export function createSection(def: WebsiteDefinition, pageSections: SectionNode[], type: SectionType, ctx: DefaultsContext = {}): SectionNode {
  const fromTemplate = templateSection(ctx.template, type);
  const vars = { nama_usaha: def.site.name, kota: def.site.contact.city || "kota Anda", kategori: ctx.industryName ?? "" };
  const content = fromTemplate
    ? (substitute(structuredClone(fromTemplate.content) as Json, vars) as Record<string, unknown>)
    : genericContent(type, ctx.stock);
  const allowed = SECTION_VARIANTS[type] as readonly string[];
  const variant = ctx.design ? resolveVariant(type, fromTemplate?.variant, ctx.design) : fromTemplate?.variant && allowed.includes(fromTemplate.variant) ? fromTemplate.variant : allowed[0]!;
  const section = {
    id: nextSectionId(def, type),
    type,
    variant,
    anchor: uniqueAnchor({ sections: pageSections }, fromTemplate?.anchor ?? defaultAnchor(type, ctx.anchors)),
    ...(SECTION_META[type].navDefault ? { showInNav: true } : {}),
    ...(fromTemplate?.navLabel ? { navLabel: fromTemplate.navLabel } : {}),
    ...(fromTemplate?.tone ? { tone: fromTemplate.tone } : {}),
    content,
  };
  return section as unknown as SectionNode;
}
